import React from 'react';
import { AlertTriangle, WifiOff, KeyRound, Gauge, ServerCrash, FileWarning } from 'lucide-react';
import { classifyAgentError } from '../utils/agentErrors';

const SEVERITY_STYLE = {
  quota:   { border: 'border-amber-500/40',  bg: 'bg-amber-500/10',  text: 'text-amber-300',  icon: <Gauge       className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> },
  auth:    { border: 'border-red-500/40',    bg: 'bg-red-500/10',    text: 'text-red-300',    icon: <KeyRound    className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> },
  model:   { border: 'border-red-500/40',    bg: 'bg-red-500/10',    text: 'text-red-300',    icon: <ServerCrash className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> },
  network: { border: 'border-orange-500/40', bg: 'bg-orange-500/10', text: 'text-orange-300', icon: <WifiOff     className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> },
  parse:   { border: 'border-yellow-500/40', bg: 'bg-yellow-500/10', text: 'text-yellow-300', icon: <FileWarning className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> },
  unknown: { border: 'border-red-500/40',    bg: 'bg-red-500/10',    text: 'text-red-300',    icon: <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> },
};

interface AgentErrorBannerProps {
  error: string | null | undefined;
  compact?: boolean; // true = single-line in history list
}

const AgentErrorBanner: React.FC<AgentErrorBannerProps> = ({ error, compact = false }) => {
  const info = classifyAgentError(error);
  if (!info) return null;

  const s = SEVERITY_STYLE[info.severity];

  if (compact) {
    return (
      <div className={`flex items-start gap-1.5 text-xs ${s.text} ${s.bg} border ${s.border} rounded px-2 py-1.5`}>
        {s.icon}
        <span className="font-medium">{info.title}</span>
        {info.hint && <span className="text-slate-400 ml-1">— {info.hint}</span>}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 text-xs ${s.bg} border ${s.border} rounded-lg p-3`}>
      <div className={`flex items-start gap-1.5 font-medium ${s.text}`}>
        {s.icon}
        {info.title}
      </div>
      {info.hint && (
        <div className="text-slate-300 leading-relaxed pl-5">{info.hint}</div>
      )}
      <details className="pl-5 mt-0.5">
        <summary className="text-slate-500 cursor-pointer hover:text-slate-400 select-none">Show raw error</summary>
        <div className="mt-1 font-mono text-slate-400 break-all whitespace-pre-wrap">{info.detail}</div>
      </details>
    </div>
  );
};

export default AgentErrorBanner;
